import { CheckmarkIcon } from "vega-icons";

import { type OptionType } from "@vega-core/models/options";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  index: number;
  isSelected?: (option: OptionType) => boolean;
  onItemClick?: (option: OptionType) => void;
  option: OptionType;
  name: string;
  withCheckmark?: boolean;
  isDisabled?: boolean;
};

export function DropdownOption({
  index,
  isSelected,
  onItemClick,
  option,
  name,
  withCheckmark = false,
  isDisabled = false,
}: Props) {
  const handleClick = () => {
    if (!isDisabled && onItemClick) {
      onItemClick(option);
    }
  };

  return (
    <div
      className={cnTwMerge("flex items-center p-3", {
        "cursor-pointer hover:bg-background/component focus:bg-background/component focus:ring-0 focus:ring-offset-0":
          !isDisabled,
        "opacity-30": isDisabled,
        "bg-background/component text-text/secondary":
          !isDisabled && isSelected?.(option),
      })}
      data-testid={`${name}-dropdown-item-${option.value}`}
      id={`item-${index}`}
      key={option.value}
      onClick={handleClick}
      role="option"
      tabIndex={1}
    >
      {isTruthy(option.icon) ? (
        <div className="flex-shrink-0 mr-4">
          {typeof option.icon === "string" ? (
            <img alt="" className="h-6 w-6" role="img" src={option.icon} />
          ) : (
            option.icon
          )}
        </div>
      ) : null}

      <div className="flex items-center justify-between w-full body-base-regular">
        <div>
          <div
            className={cnTwMerge("text-text/primary", option.className ?? "", {
              "text-xs": option.icon,
            })}
          >
            {option.label}
          </div>
          {isTruthy(option.description) ? (
            <div className="text-text/secondary">{option.description}</div>
          ) : null}
        </div>
        {!isDisabled && withCheckmark && isSelected?.(option) ? (
          <CheckmarkIcon className="shrink-0 text-text/interactive" />
        ) : null}
      </div>
    </div>
  );
}
